import {
  resolveBacktimeAnchor,
  planBacktime,
  backtimeSegment,
  type BacktimeContext,
  type BacktimeWrite,
} from './backtime'
import { truncateText } from '../../../utils/truncate-text'
import type { TimingFixAction, TimingFixPlan } from './timingFixTypes'

/**
 * Build the gap/overlap "Fix" menu plan for a hard-start cue.
 *
 * The backtime MATH lives in `./backtime`, the client being its only consumer.
 * This module is the PRESENTATION layer on top: it calls the resolver / planner
 * and turns each strategy into a labelled, formatted `TimingFixPlan` for the
 * dropdown. It performs no I/O and no timeline math of its own.
 *
 * The planner's `BacktimeWrite` (`{ kind: 'cue_start_time' | 'cue_duration',
 * … }`) is mapped to the menu's `action` shape (`patch-cue-start-time` /
 * `patch-cue-duration`), which CueItem binds to a mutation.
 */

/** Map a planned BacktimeWrite → the menu's action shape. */
function toAction(write: BacktimeWrite): TimingFixAction {
  if (write.kind === 'cue_duration') {
    return {
      kind: 'patch-cue-duration',
      cueId: write.cueId,
      duration: write.cueDuration,
      isFirstCue: write.isFirstCue,
    }
  }
  return {
    kind: 'patch-cue-start-time',
    cueId: write.cueId,
    startTime: write.startTime,
    startDatePlus: write.startDatePlus,
    isFirstCue: write.isFirstCue,
  }
}

export interface ComputeTimingFixesArgs {
  ctx: BacktimeContext
  anchorCueId: string
  type: 'Gap' | 'Overlap'
  /** Format a Date for time labels. */
  formatTime: (date: Date) => string
  /** Format milliseconds for duration labels. */
  formatDuration: (ms: number) => string
  /** Resolve a cue's title for the absorb submenu. */
  getCueTitle: (cueId: string) => string
  /**
   * True while the show is live/ended (a runner is driving the timeline). When
   * set, fixes whose write lands on an already-played cue can't take effect:
   * the runner has locked those times, so the field write is a silent no-op and
   * the gap never closes. Such fixes are emitted `disabled` with an explanatory
   * hint instead of an action. Defaults to false (planning mode).
   */
  isRunning?: boolean
  /**
   * Whether a cue is past or currently active (i.e. runner-locked, not editable
   * into the future). Only consulted when `isRunning`. Used to disable absorb
   * sub-items that target a played cue. Defaults to "nothing played".
   */
  isCuePlayed?: (cueId: string) => boolean
}

export function computeTimingFixes({
  ctx,
  anchorCueId,
  type,
  formatTime,
  formatDuration,
  getCueTitle,
  isRunning = false,
  isCuePlayed = () => false,
}: ComputeTimingFixesArgs): TimingFixPlan[] {
  const anchor = resolveBacktimeAnchor(ctx, anchorCueId)
  if (!anchor) return []

  const { differenceMs } = anchor
  const magnitude = formatDuration(Math.abs(differenceMs))
  const closes = type === 'Gap' ? `close the ${magnitude} gap` : `remove the ${magnitude} overlap`

  const fixes: TimingFixPlan[] = []

  // 1. Move this hard start to meet the cues above.
  const moved = planBacktime({ ctx, anchorCueId, anchor, strategy: 'move_this_hard_start' })
  if ('write' in moved && moved.write.kind === 'cue_start_time') {
    const name = truncateText(getCueTitle(moved.write.cueId) || 'Untitled cue')
    fixes.push({
      key: 'move-hard-start',
      label: `Move "${name}" to ${formatTime(moved.write.startTime)}`,
      detail: `Moves the hard start to ${closes}. Durations unchanged.`,
      action: toAction(moved.write),
    })
  }

  // 2. Move the governing hard start so the run above lands on this cue (backtime).
  // The governing hard start is always upstream of the anchor, so once the show
  // is live/ended it has already played: the runner has locked its time and a
  // field write can't move it. Offer the option greyed out (so the user learns
  // why) instead of silently no-op-ing.
  const shifted = planBacktime({ ctx, anchorCueId, anchor, strategy: 'move_preceding_hard_start' })
  if ('write' in shifted && shifted.write.kind === 'cue_start_time') {
    const name = truncateText(getCueTitle(shifted.write.cueId) || 'Untitled cue')
    fixes.push({
      key: 'shift-show-start',
      label: `Move "${name}" to ${formatTime(shifted.write.startTime)}`,
      detail: `Moves the previous hard start so cues land here, to ${closes}. Durations unchanged.`,
      disabled: isRunning,
      hint: isRunning
        ? 'Can\'t move the show start while the show is running or ended, because the earlier cues have already played. Move this hard start or shorten a cue above instead.'
        : undefined,
      action: isRunning ? undefined : toAction(shifted.write),
    })
  }

  // 3. Absorb the difference into a chosen cue in the governing segment (submenu).
  const segment = backtimeSegment(ctx, anchorCueId, anchor.governingCueId)
  if (segment.length > 0) {
    const submenu: TimingFixPlan[] = segment.map((above, i) => {
      const planned = planBacktime({
        ctx,
        anchorCueId,
        anchor,
        strategy: 'adjust_cue_duration',
        absorbCueId: above.id,
      })
      const write = 'write' in planned && planned.write.kind === 'cue_duration' ? planned.write : null
      const title = getCueTitle(above.id) || 'Untitled cue'
      const positionLabel = `${i + 1}. ${truncateText(title)}`
      // A played (past/active) cue is runner-locked: changing its stored
      // duration won't move the live timeline, so the fix would silently do
      // nothing. Disable it with its own hint (takes precedence over the
      // overlap-too-long case, which is a separate reason).
      const played = isRunning && isCuePlayed(above.id)
      const disabled = played || !write
      const hint = played
        ? 'This cue has already played, so its length is locked while the show is live. Pick a later cue.'
        : 'Overlap is longer than this cue. Pick another cue or a different fix.'
      return {
        key: `absorb-${above.id}`,
        label: write
          ? `${positionLabel} → ${formatDuration(write.cueDuration)}`
          : positionLabel,
        detail: !disabled && write
          ? `Sets "${title}" to ${formatDuration(write.cueDuration)} to ${closes}.`
          : undefined,
        disabled,
        hint,
        action: disabled ? undefined : toAction(write!),
      }
    })
    fixes.push({
      key: 'absorb-into-cue',
      label: 'Change duration of…',
      submenu,
    })
  }

  // 4. Fill the gap with a new cue instead of shifting anything. The new cue
  // lands directly above the anchor, so nothing already on the timeline moves;
  // that also makes it safe while the show is live (it's inserted ahead of a
  // hard start that hasn't played). Only works on a Gap, since an overlap is
  // negative time that no cue can absorb, but the option is still listed,
  // greyed out, so the user learns why rather than wondering where it went.
  const anchorName = truncateText(getCueTitle(anchorCueId) || 'Untitled cue')
  const fillable = type === 'Gap' && differenceMs > 0
  fixes.push({
    key: 'insert-cue',
    label: fillable
      ? `Insert ${magnitude} cue before "${anchorName}"`
      : `Insert a cue before "${anchorName}"`,
    detail: fillable
      ? `Adds a new ${magnitude} cue above "${anchorName}" to ${closes}. Nothing else moves.`
      : undefined,
    disabled: !fillable,
    hint: fillable
      ? undefined
      : 'Only fixes a gap. An overlap means the cues above run too long, so adding another cue would make it worse. Shorten a cue above or move a hard start instead.',
    action: fillable
      ? {
          kind: 'insert-cue-before',
          beforeCueId: anchorCueId,
          duration: differenceMs,
        }
      : undefined,
  })

  return fixes
}
